/** @jsxImportSource @emotion/react */
import { Hidden as MuiHidden, useMediaQuery } from '@material-ui/core';
import { Pagination as MuiPagination } from '@material-ui/lab';
import { breakpoints } from '@zip/business-components';
import { SummaryCards, TableRows } from 'components';
import CheckboxTable from 'components/checkbox-table/CheckboxTable';
import { useMerchantData, useSnackbar } from 'contexts';
import { PageAction } from 'enums';
import { Constants } from 'global';
import { CompleteMfaDialogModule } from 'modules';
import {
  ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { MerchantUser } from 'types';
import { logError, logEvent, useUserFunctions } from 'utils';
import * as styles from './UsersTable.styles';
import { UsersTableModuleProps } from './UsersTableModuleProps';
import { UsersTableRef } from './UsersTableRef';

const UsersTableModule = forwardRef((props: UsersTableModuleProps, ref) => {
  const { error, success } = useSnackbar();
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const { searchValue, show2StepVerification, getUserActions } = props;
  const {
    merchantIdentity,
    isCompanyUser,
    currentUserId,
    updateCurrentUserMfaEnrollmentStatus,
  } = useMerchantData();
  const UserFunctions = useUserFunctions();

  const [users, setUsers] = useState<MerchantUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPageCount, setTotalPageCount] = useState<number>(0);
  const [totalRowCount, setTotalRowCount] = useState<number>(0);
  const PAGE_SIZE = 10;

  // Selection
  // isSelectAllInAccount - Select all users for the currently selected Merchant in MerchantSelector
  // Company users see: All Company users + currently selected Merchant users
  const [isSelectAllInAccount, setIsSelectAllInAccount] =
    useState<boolean>(false);
  const [selected, setSelected] = useState<MerchantUser[]>([]);

  const MFA_TOOLTIP_JSX = (
    <>
      <span className="body3">
        Enable 2-step verification for extra security
      </span>
      <br />
      <span className="body4">
        Users will be verified using a code sent via mobile or email when
        signing in.
      </span>
    </>
  );

  let columns = isCompanyUser
    ? [
        { name: 'Name' },
        { name: 'Type' },
        { name: 'Role' },
        { name: 'Email' },
        { name: '2-step verification', tooltipContent: MFA_TOOLTIP_JSX },
        { name: '' },
      ]
    : [
        { name: 'Name' },
        { name: 'Role' },
        { name: 'Email' },
        { name: '2-step verification', tooltipContent: MFA_TOOLTIP_JSX },
        { name: '' },
      ];

  if (!show2StepVerification) {
    columns = columns.filter((column) => column.name !== '2-step verification');
  }

  const getUsers = async (page = 1): Promise<void> => {
    setCurrentPage(page);

    const query = {
      searchValue,
      page: page,
      pageSize: PAGE_SIZE,
    };

    setIsLoading(true);

    logEvent(PageAction.searchUsers, query);
    await UserFunctions.getUsers(query)
      .then((res) => {
        setUsers(
          res?.paginatedUsers?.items?.map((user) => ({
            ...user,
            roleId: user.roleId.toUpperCase(),
          }))
        );
        setTotalPageCount(res?.paginatedUsers?.totalPageCount);
        setTotalRowCount(res?.paginatedUsers?.totalRowCount);
      })
      .catch((err) => {
        if (!err?.stack?.includes(Constants.errors.abortedSignal)) {
          logError(PageAction.searchUsers, err, query);
        }
        setUsers([]);
        setTotalPageCount(1);
        setTotalRowCount(0);
      });

    // reset selection on page change
    setSelected([]);
    setIsSelectAllInAccount(false);

    setIsLoading(false);
  };

  // Passing userProfileId means it's called from individual toggle, otherwise it's from bulk selection
  const updateMfaEnrollments = async (
    enableMfa: boolean,
    userProfileId?: number
  ): Promise<void> => {
    if (!show2StepVerification) {
      return;
    }

    setIsLoading(true);

    let userProfileIdsToUpdate = [];
    if (userProfileId) {
      userProfileIdsToUpdate = [userProfileId]; // Enable individual
    } else if (!isSelectAllInAccount && selected.length > 0) {
      userProfileIdsToUpdate = selected.map((x) => x.userProfileId); // Enable from list of selected
    } else if (isSelectAllInAccount) {
      userProfileIdsToUpdate = []; // Empty UserProfileIds intended, will use Merchant/Branch Id from Auth Token/Header
    }

    const trackingAttributes = {
      enable: enableMfa,
      updateUserCount: isSelectAllInAccount
        ? totalRowCount
        : userProfileIdsToUpdate.length,
      method: userProfileIdsToUpdate.length === 1 ? 'Individual' : 'Bulk',
    };

    await UserFunctions.updateMfaEnrollments(enableMfa, userProfileIdsToUpdate)
      .then((res: any) => {
        if (res?.success) {
          success('Changes saved');
          logEvent(PageAction.mfaUserManagementUpdate, trackingAttributes);
        } else {
          error('Something went wrong');
        }
      })
      .catch((err) => {
        if (!err?.stack?.includes(Constants.errors.abortedSignal)) {
          error('Something went wrong');
        }
      });

    await getUsers(currentPage);

    if (currentUserId == userProfileId?.toString()) {
      await updateCurrentUserMfaEnrollmentStatus().catch(() => {
        error('Something went wrong');
      });
    }

    setIsLoading(false);
  };

  const handlePageChange = (_event: unknown, newPage: number): void => {
    setCurrentPage(newPage);
  };

  // Selection
  const bulkActions = [
    {
      label: 'Enable 2-Step Verification',
      onClick: async (): Promise<void> => await updateMfaEnrollments(true),
    },
    {
      label: 'Disable 2-Step Verification',
      onClick: async (): Promise<void> => await updateMfaEnrollments(false),
    },
  ];

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>): void => {
    if (!show2StepVerification) {
      return;
    }

    if (selected.length > 0) {
      setSelected([]);
      return;
    }

    if (event.target.checked) {
      setSelected(users);
      return;
    }

    setSelected([]);
  };

  const handleSelectAllInAccountClick = (value: boolean): void => {
    if (!show2StepVerification) {
      return;
    }

    setIsSelectAllInAccount(value);

    if (!value) {
      // On false, clear all selection
      setSelected([]);
    }
  };

  const handleRowSelected = (user: MerchantUser): void => {
    const selectedIndex = selected.indexOf(user);

    const newSelected =
      selectedIndex === -1
        ? [...selected, user] // Add entry if not exists
        : selected.filter((_, index) => index !== selectedIndex); // Remove entry if already exists

    setSelected(newSelected);
    if (isSelectAllInAccount && newSelected.length < PAGE_SIZE) {
      setIsSelectAllInAccount(false);
    }
  };

  // MFA
  const handleMfaSwitchToggled = (event: any, user: MerchantUser): void => {
    updateMfaEnrollments(event.target.checked, user.userProfileId);
  };

  useEffect(() => {
    getUsers(1);

    return (): void => UserFunctions.resetGetRequests();
  }, [searchValue, merchantIdentity]);

  useEffect(() => {
    getUsers(currentPage);

    return (): void => UserFunctions.resetGetRequests();
  }, [currentPage]);

  useImperativeHandle(
    ref,
    (): UsersTableRef => ({
      getUsers,
    })
  );

  return (
    <>
      <MuiHidden smDown>
        <CheckboxTable
          columns={columns}
          selectedCount={selected.length}
          rowsInPageCount={users.length}
          totalRowCount={totalRowCount}
          pageSize={PAGE_SIZE}
          onSelectAllClick={handleSelectAllClick}
          onSelectAllInAccountClick={handleSelectAllInAccountClick}
          bulkActions={bulkActions}
          isSelectAllInAccount={isSelectAllInAccount}
          isLoading={isLoading}
          enableBulkAction={show2StepVerification}
        >
          {users &&
            users?.map((user) => (
              <TableRows.User
                user={user}
                selected={selected.indexOf(user) >= 0}
                actions={getUserActions(user)}
                show2StepVerification={show2StepVerification}
                onClick={(): void => handleRowSelected(user)}
                onMfaSwitchToggled={handleMfaSwitchToggled}
              />
            ))}
        </CheckboxTable>
      </MuiHidden>
      <MuiHidden mdUp>
        {users &&
          users?.map((user) => (
            <SummaryCards.User
              key={`mobile-row-${user.userProfileId}`}
              user={user}
              actions={getUserActions(user)}
              selected={selected.indexOf(user) >= 0}
              onMfaSwitchToggled={handleMfaSwitchToggled}
              show2StepVerification={show2StepVerification}
            />
          ))}
        {!isLoading && users?.length < 1 && (
          <p style={{ textAlign: 'center' }}>
            No users found matching the specified filters
          </p>
        )}
      </MuiHidden>

      {!isLoading && (
        <MuiPagination
          count={totalPageCount}
          page={currentPage}
          css={styles.pagination}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
          variant="outlined"
          size="large"
          siblingCount={isMobile ? 0 : 1} // shorter pagination on mobile view
        />
      )}

      <CompleteMfaDialogModule />
    </>
  );
});

export default UsersTableModule;
